import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/cogniflock_logo_full.svg";
import SlackSyncButton from "./SlackSyncButton";
import { isDevelopment } from "../utils/config";

const SidebarItem = ({ icon, label, onClick, isSelected }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center w-full gap-3 px-2 py-1.5 text-sm font-normal rounded-md transition-colors ${
        isSelected
          ? "bg-grey-20 text-black-0"
          : "text-black-0 hover:bg-grey-20"
      }`}
    >
      <div className={`w-5 h-5 flex items-center justify-center ${isSelected ? "text-black-0" : ""}`}>
        {icon}
      </div>
      <span>{label}</span>
    </button>
  );
};

const Sidebar = ({ user, organization, organizations }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Create a default date range for the sync button 
  const defaultDateRange = {
    startDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date;
    })(),
    endDate: new Date(),
    range: "last7days"
  };

  const handleSignOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };


  
  const handleSyncComplete = (timestamp) => {
    // Refresh the current page to show new data
    window.location.reload();
  };

  // Function to check if a path is currently active
  const isPathActive = (path) => {
    return location.pathname === path;
  };

  // Tabler icons
  const icons = {
    // Layout icon for Dashboard
    dashboard: (
      <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-layout-2"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 4m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v1a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M4 13m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v3a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M14 4m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v3a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M14 15m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v1a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /></svg>
    ),
    // Hierarchy-3 icon for Network Analysis
    network: (
      <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-polygon"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M19 8m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M5 11m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M15 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M6.5 9.5l3.5 -3" /><path d="M14 5.5l3 1.5" /><path d="M18.5 10l-2.5 7" /><path d="M13.5 17.5l-7 -5" /></svg>
    ),
    // Keyboard-hide icon for Engagement
    engagement: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-keyboard-hide"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M2 3m0 2a2 2 0 0 1 2 -2h16a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-16a2 2 0 0 1 -2 -2z" /><path d="M6 7l0 .01" /><path d="M10 7l0 .01" /><path d="M14 7l0 .01" /><path d="M18 7l0 .01" /><path d="M6 11l0 .01" /><path d="M18 11l0 .01" /><path d="M10 11l4 0" /><path d="M10 21l2 -2l2 2" /></svg>
    ),
    // Mood-smile-beam icon for Sentiment
    sentiment: (
      <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-mood-smile"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M9 10l.01 0" /><path d="M15 10l.01 0" /><path d="M9.5 15a3.5 3.5 0 0 0 5 0" /></svg>
    ),
    // Speakerphone icon for Tone
    tone: (
      <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-speakerphone"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M18 8a3 3 0 0 1 0 6" /><path d="M10 8v11a1 1 0 0 1 -1 1h-1a1 1 0 0 1 -1 -1v-5" /><path d="M12 8h0l4.524 -3.77a.9 .9 0 0 1 1.476 .692v12.156a.9 .9 0 0 1 -1.476 .692l-4.524 -3.77h-8a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h8" /></svg>
    ),
    // Ballpen icon for Clarity
    clarity: (
      <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-ballpen"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M14 6l7 7l-4 4" /><path d="M5.828 18.172a2.828 2.828 0 0 0 4 0l10.586 -10.586a2 2 0 0 0 0 -2.829l-1.171 -1.171a2 2 0 0 0 -2.829 0l-10.586 10.586a2.828 2.828 0 0 0 0 4z" /><path d="M4 20l1.768 -1.768" /></svg>
    ),
    // Chart-bubble icon for Topic Clustering
    clustering: (
      <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-chart-bubble"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 16m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M16 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M14.5 7.5m-4.5 0a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0 -9 0" /></svg>
    ),
    // Settings icon for Settings
    settings: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-settings"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" /><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" /></svg>
    ),
    // Plug icon for Integrations
    integrations: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-plug-connected"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 12l5 5l-1.5 1.5a3.536 3.536 0 1 1 -5 -5l1.5 -1.5" /><path d="M17 12l-5 -5l1.5 -1.5a3.536 3.536 0 1 1 5 5l-1.5 1.5" /><path d="M3 21l2.5 -2.5" /><path d="M18.5 5.5l2.5 -2.5" /><path d="M10 11l-2 2" /><path d="M13 14l-2 2" /></svg>
    ),
    // Rocket icon for Upgrade Plan
    upgrade: (
      <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-rocket"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 13a8 8 0 0 1 7 7a6 6 0 0 0 3 -5a9 9 0 0 0 6 -8a3 3 0 0 0 -3 -3a9 9 0 0 0 -8 6a6 6 0 0 0 -5 3" /><path d="M7 14a6 6 0 0 0 -3 6a6 6 0 0 0 6 -3" /><path d="M15 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /></svg>
    ),
    // Test tube icon for Testing
    testing: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-test-pipe"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M20 8.04l-12.122 12.124a2.857 2.857 0 1 1 -4.041 -4.04l12.122 -12.124" /><path d="M7 13h8" /><path d="M19 15l1.5 1.6a2 2 0 1 1 -3 0l1.5 -1.6z" /><path d="M15 3l6 6" /></svg>
    ),
    // Refresh icon for Sync
    sync: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-refresh"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" /><path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" /></svg>
    )
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  return (
    <>
      {/* Spacer so page layouts using `flex` don't sit under fixed sidebar */}
      <div className="w-60 min-w-60 shrink-0" aria-hidden="true" />

      <aside className="fixed left-0 top-0 w-60 min-w-60 p-4 border-r border-grey-20 flex flex-col h-screen h-dvh z-20 overflow-hidden bg-transparent">
        <div className="mb-5 px-2 flex">
          <img src={logo} alt="Logo" className="h-6 w-auto" />
        </div>

        <div className="flex flex-col gap-5 flex-grow min-h-0">
          {/* Overview Section */}
          <div className="flex flex-col gap-2">
            <SidebarItem 
              icon={icons.dashboard} 
              label="Dashboard" 
              onClick={() => navigate('/dashboard')} 
              isSelected={isPathActive('/dashboard') || isPathActive('/')}
            />
            
            {/* Comment out Testing page but keep it for later use */}
            {/* 
            <SidebarItem 
              icon={icons.testing} 
              label="Testing" 
              onClick={() => navigate('/testing')} 
              isSelected={isPathActive('/testing')}
            />
            */}
            
            <SidebarItem 
              icon={icons.network} 
              label="Relationship analysis" 
              onClick={() => navigate('/network-analysis')} 
              isSelected={isPathActive('/network-analysis')}
            />

            <SidebarItem 
              icon={icons.engagement} 
              label="Engagement" 
              onClick={() => navigate('/engagement')} 
              isSelected={isPathActive('/engagement')}
            />
            <SidebarItem 
              icon={icons.sentiment} 
              label="Sentiment analysis" 
              onClick={() => navigate('/sentiment-analysis')} 
              isSelected={isPathActive('/sentiment-analysis')}
            />
            <SidebarItem 
              icon={icons.tone} 
              label="Tone" 
              onClick={() => navigate('/tone')} 
              isSelected={isPathActive('/tone')}
            />
            <SidebarItem 
              icon={icons.clarity} 
              label="Clarity" 
              onClick={() => navigate('/clarity')} 
              isSelected={isPathActive('/clarity')}
            />
            <SidebarItem 
              icon={icons.clustering} 
              label="Topic clustering" 
              onClick={() => navigate('/topic-clustering')} 
              isSelected={isPathActive('/topic-clustering')}
            />
          </div>

          {/* Bottom Section - will stick to bottom */}
          <div className="mt-auto flex flex-col gap-2">
            <SidebarItem 
              icon={icons.integrations} 
              label="Integrations" 
              onClick={() => navigate('/integrations')} 
              isSelected={isPathActive('/integrations')}
            />
            <SidebarItem 
              icon={icons.settings} 
              label="Settings" 
              onClick={() => navigate('/settings')} 
              isSelected={isPathActive('/settings')}
            />
            {isDevelopment() && (
              <SidebarItem
                icon={icons.testing}
                label="Dev tools"
                onClick={() => navigate('/dev-tools')}
                isSelected={isPathActive('/dev-tools')}
              />
            )}
            {/* 
            <SidebarItem 
              icon={icons.upgrade} 
              label="Upgrade plan" 
              onClick={() => {}} 
              isSelected={false}
            />
            */}

            {/* User Profile  */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                className="flex items-center w-full gap-3 px-2 py-1 text-sm font-normal text-black-0 rounded-md hover:bg-grey-10 transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white-f text-xs text-black-0">
                  {user?.username ? user.username.charAt(0).toUpperCase() : "U"}
                </div>
                <span>{user?.username || "User"}</span>
              </button>

              {isUserDropdownOpen && (
                <div className="absolute bottom-full mb-1 w-48 bg-white-f border border-grey-20 rounded-md shadow-subtle">
                  <div className="py-1">
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-grey-60 hover:bg-grey-10"
                      onClick={() => window.open('https://cogniflock.com/terms-and-conditions.html', '_blank')}
                    >
                      Terms and conditions
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-grey-60 hover:bg-grey-10"
                      onClick={() => window.open('https://cogniflock.com/privacy-policy.html', '_blank')}
                    >
                      Privacy policy
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-grey-60 hover:bg-grey-10"
                      onClick={handleSignOut}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar; 